import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid2 as Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../api/auth/authSlice';
import PartnersSelectField from './PartnersSelectField';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { regionFeatures } from '../../utils/features';
import { clearSelectedPartner, fetchPartnerById } from '../../api/partners/partnerSlice';
import appConfig from '../../config/appConfig';

const OrganizationForm = ({
  initialData,
  onSubmit,
  onCancel,
  buttonText = 'Submit',
  disabledFields = [],
  is_embedded_partner = false,
}) => {
  const dispatch = useDispatch();
  let user = useSelector(selectUser);
  const [formData, setFormData] = useState(initialData);
  const { selectedPartner } = useSelector((state) => state.partners);
  const regionFeatureSet = regionFeatures[appConfig.marminRegion] ?? {};

  useEffect(() => {
    if (initialData.partner_id) {
      dispatch(fetchPartnerById(initialData.partner_id));
    } else {
      dispatch(clearSelectedPartner());
    }
  }, [dispatch, initialData]);

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const onSubmitLocal = async (data) => {
    const payload = {
      ...data,
      contact_emails: data.contact_emails?.filter((email) => email.trim() !== '') || [],
      partner_id: user.role === 'partner' ? user._id : formData.partner_id,
    };

    onSubmit(payload);
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        name: Yup.string().required('Organization name is required'),
        email: Yup.string().email().required('Organization email is required'),
        contact_emails: Yup.array().of(Yup.string().email('Invalid email format')).nullable(),
        features: Yup.object(),
      }),
    ),
    defaultValues: {
      ...initialData,
      features:
        {
          ...Object.fromEntries(Object.entries(regionFeatureSet).map(([key, value]) => [key, value.defaultValue])),
          ...initialData?.features,
        } || {},
    },
  });

  const {
    fields: contact_emails,
    append: addContactEmail,
    remove: removeContactEmail,
  } = useFieldArray({
    control,
    name: 'contact_emails',
  });

  return (
    <Box sx={{ mt: 6 }}>
      <form onSubmit={handleSubmit(onSubmitLocal)}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  id={'name'}
                  label="Organization Name"
                  fullWidth
                  placeholder={'Enter Your Organization Name'}
                  size="small"
                  {...field}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={disabledFields.includes('name')}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Email"
                  fullWidth
                  placeholder={'Enter Organization Email'}
                  type="email"
                  size="small"
                  {...field}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={disabledFields.includes('email')}
                />
              )}
            />
          </Grid>
          {user.role === 'admin' && !is_embedded_partner && (
            <Grid size={6}>
              <PartnersSelectField
                initialValue={formData.partner_id}
                onChange={(value) => setFormData({ ...formData, partner_id: value })}
              />
            </Grid>
          )}
        </Grid>
        <Divider textAlign="center" sx={{ mt: '2rem', mb: '2rem' }}>
          <Typography variant="h6" color="text.secondary">
            Contact Emails
          </Typography>
        </Divider>
        <Grid container columnSpacing={{ xs: 1, sm: 1 }} rowSpacing={2}>
          {contact_emails.map((email, index) => (
            <React.Fragment key={`${email.id}`}>
              <Grid size={{ xs: 9, sm: 9, md: 9, lg: 5 }}>
                <Controller
                  name={`contact_emails.${index}`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={'Enter Contact Email'}
                      label={`Email ${index + 1}`}
                      {...field}
                      error={!!errors.contact_emails}
                      helperText={errors.contact_emails ? errors.contact_emails[index]?.message : ''}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 3, sm: 3, md: 3, lg: 1 }}>
                <Button
                  color="error"
                  onClick={() => {
                    removeContactEmail(index);
                  }}
                  disabled={contact_emails.length === 1 || disabledFields.includes('contact_emails')}
                  variant="outlined"
                >
                  Remove
                </Button>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
        <Box
          sx={{
            mt: 2,
          }}
        >
          {!disabledFields.includes('contact_emails') && (
            <Button variant="outlined" color="primary" onClick={() => addContactEmail('')}>
              Add Another Email
            </Button>
          )}
        </Box>
        {!selectedPartner?.is_embedded && (
          <>
            <Divider textAlign="center" sx={{ mt: '2rem', mb: '2rem' }}>
              <Typography variant="h6" color="text.secondary">
                Features
              </Typography>
            </Divider>
            <Grid container>
              {Object.entries(regionFeatureSet).map(([key, value]) => (
                <Grid size={4} key={key}>
                  <FormGroup>
                    <Controller
                      name={`features.${key}`}
                      control={control}
                      disabled={disabledFields.includes('features')}
                      render={({ field }) => (
                        <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={value.label} />
                      )}
                    />
                  </FormGroup>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        <Box
          sx={{
            float: 'right',
            mt: 2,
            right: 0,
          }}
        >
          <Button variant="outlined" color="info" sx={{ mr: 2 }} onClick={onCancel}>
            Cancel
          </Button>

          <Button type="submit" variant="contained" color="primary">
            {buttonText}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default OrganizationForm;
